import React from "react";
import { styled } from "@mui/material/styles";
import PanelInner from "components/odl-v2/Layout/PanelInner";
import Guard from "components/Guard/Guard";
import { css, keyframes } from "@emotion/react";

export type PanelProps = {
  "data-testid"?: string;
  hasInnerContainer?: boolean;
  hasError?: boolean;
  /** @description Objective Intelligence gradient AI effect */
  hasIntelligenceGradient?: boolean;
  thickness?: "thin" | "normal" | "bold";
};

const Panel = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<PanelProps & React.HTMLAttributes<HTMLDivElement>>
>(
  (
    {
      children,
      "data-testid": dataTestId,
      hasInnerContainer = true,
      hasError = false,
      hasIntelligenceGradient = false,
      thickness = "thin",
      ...otherProps
    },
    ref
  ) => {
    return (
      <StyledPanelContainerOuter
        data-testid={dataTestId || "Panel"}
        $hasError={hasError}
        $hasIntelligenceGradient={hasIntelligenceGradient}
        $thickness={thickness}
        ref={ref}
        {...otherProps}
      >
        <Guard condition={hasInnerContainer} fallback={children}>
          <PanelInner>{children}</PanelInner>
        </Guard>
      </StyledPanelContainerOuter>
    );
  }
);

const gradientShift = keyframes`
  0%,
  25% {
    background-position: 0% 0%;
  }

  50%,
  75% {
    background-position: 0% 100%;
  }

  100% {
    background-position: 0% 0%;
  }
`;

const StyledPanelContainerOuter = styled("div", {
  shouldForwardProp: (prop) => !["$hasError", "$hasIntelligenceGradient", "$thickness"].includes(prop.toString()),
})<{ $hasError: boolean; $hasIntelligenceGradient: boolean; $thickness: string }>(
  ({ theme, $hasError, $hasIntelligenceGradient, $thickness }) => css`
    background-color: ${theme.palette.background.default};
    ${
      $thickness === "thin" &&
      css`
        padding: 24px;
        border-radius: 16px;

        ${theme.breakpoints.down("xs")} {
          padding: 8px;
        }
      `
    }
    ${
      $thickness === "normal" &&
      css`
        padding: 36px;
        border-radius: 16px;

        ${theme.breakpoints.down("xs")} {
          padding: 12px;
        }
      `
    }
    ${
      $thickness === "bold" &&
      css`
        padding: 54px;
        border-radius: 16px;

        ${theme.breakpoints.down("xs")} {
          padding: 16px;
        }
      `
    }
    ${
      $hasError &&
      css`
        background-color: #f7e4e6;
        border: 1px dashed #ff7381;
      `
    }
    ${
      $hasIntelligenceGradient &&
      css`
        background: linear-gradient(
          180deg,
          rgba(96, 159, 30, 0.4),
          rgba(19, 171, 167, 0.4),
          rgba(6, 123, 214, 0.4),
          rgba(19, 171, 167, 0.4),
          rgba(96, 159, 30, 0.4)
        );
        background-size: 100% 200%;
        animation: ${gradientShift} 8s infinite linear;
      `
    }
  `
);

export default Panel;
